import { Wallet, ShieldCheck, LifeBuoy, Repeat, Car, CalendarClock } from 'lucide-react';
import { SectionHeader, CheckList } from '@/components/brand/BrandKit';
import { LeadConnectForm, LEASING_FORM_ID } from '@/components/LeadConnectForm';

const GAINS = [
  { icon: Car, title: 'A newer car, more often', desc: 'Drive a well-maintained, recent-model vehicle and step into a new one at the end of your term.' },
  { icon: Wallet, title: 'One simple monthly rate', desc: 'A single payment, inclusive of 5% VAT — no large upfront outlay tying up your cash.' },
  { icon: ShieldCheck, title: 'The essentials, handled', desc: 'Registration, comprehensive insurance, scheduled servicing, maintenance and 24/7 roadside assistance are included.' },
  { icon: Repeat, title: 'No resale worries', desc: 'We carry the financing, depreciation and end-of-lease disposal, so the car\'s future value is never your problem.' },
];

const STEPS = [
  { n: '01', t: 'Choose your car', d: 'Tell us the vehicle or category, your preferred term and how much you drive each year.' },
  { n: '02', t: 'Get a tailored quote', d: 'We confirm availability and share your monthly rate, mileage allowance, deposit and key lease details.' },
  { n: '03', t: 'Apply & sign', d: 'Once approved and signed, we arrange handover at a location that suits you.' },
  { n: '04', t: 'Drive with confidence', d: 'Enjoy the drive. At the end of the term, return the car or start your next lease.' },
];

const INCLUDED = [
  'Vehicle registration and renewal',
  'Comprehensive insurance, subject to policy terms',
  'Scheduled servicing and routine maintenance',
  '24/7 roadside assistance',
  'Eligible replacement mobility, subject to availability',
  'Delivery and collection within the agreed service area',
  'Financing, depreciation and disposal management',
];

const NOTES = [
  'Tolls (Salik) and traffic fines',
  'Mileage beyond your agreed allowance',
  'Applicable insurance excess',
  'Damage beyond fair wear and tear',
  'A refundable security deposit, confirmed in your quote',
];

const TERMS = [
  { term: '12 months', km: '20,000 km / year' },
  { term: '24 months', km: '30,000 km / year' },
  { term: '36 months', km: '40,000 km / year' },
  { term: '48 months', km: 'Mileage to suit you' },
];

export function PersonalLease() {
  return (
    <section id="personal-lease" data-testid="personal-lease" className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Positioning + value props */}
        <div className="max-w-3xl">
          <SectionHeader chip="Why Personal Lease" title="Own the drive, not the hassle."
            desc="With Personal Lease from Eurogulf Mobility, powered by Europcar, you pick the car, the term and the mileage. We take on everything else that usually comes with owning a vehicle — so the only thing left for you is the road ahead." />
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GAINS.map((g) => (
            <div key={g.title} className="rounded-xl border border-black/5 bg-[#FAFAFA] p-5">
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#EE5A01] text-white"><g.icon className="w-5 h-5" /></span>
              <h4 className="mt-4 font-heading font-bold text-sm text-[#121212]">{g.title}</h4>
              <p className="mt-1.5 font-body text-xs text-[#666666] leading-relaxed">{g.desc}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mt-16 sm:mt-24">
          <SectionHeader chip="How It Works" title="Four simple steps to your lease." align="center" />
          <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s) => (
              <li key={s.n} className="card-light p-6">
                <span className="grid h-12 w-12 place-items-center rounded-lg bg-[#EE5A01] font-heading font-black text-lg text-white">{s.n}</span>
                <h4 className="mt-5 font-heading font-bold text-base text-[#121212]">{s.t}</h4>
                <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Included + notes */}
        <div className="mt-16 sm:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
          <div className="rounded-2xl border border-black/5 bg-[#FAFAFA] p-6 sm:p-8">
            <h3 className="font-heading font-bold text-xl text-[#121212] mb-5">What's in your monthly rate</h3>
            <CheckList items={INCLUDED} />
          </div>
          <div className="rounded-2xl border border-black/5 bg-white p-6 sm:p-8">
            <h3 className="font-heading font-bold text-xl text-[#121212] mb-3">What stays with you</h3>
            <p className="font-body text-sm text-[#666666] mb-5">A few usage-based items remain your responsibility:</p>
            <ul className="space-y-3">
              {NOTES.map((n) => (
                <li key={n} className="flex items-start gap-3 font-body text-sm text-[#121212]"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#EE5A01] flex-shrink-0" />{n}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Terms + single quote form */}
        <div className="mt-16 sm:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-6">
            <SectionHeader chip="Flexible Terms" title="Pick the term, we'll tailor the rest."
              desc="Choose a term from 12 to 48 months with a mileage allowance to match how you drive. Your exact vehicle, monthly rate and deposit are confirmed in your personalised quote — all rates include 5% VAT." />
            <div className="mt-8 grid grid-cols-2 gap-4">
              {TERMS.map((t) => (
                <div key={t.term} className="card-light p-5 flex items-start gap-3">
                  <CalendarClock className="w-5 h-5 text-[#EE5A01] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-black text-lg text-[#121212] leading-none">{t.term}</p>
                    <p className="mt-1.5 font-body text-xs text-[#666666]">{t.km}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 font-body text-xs text-[#888] leading-relaxed">By submitting an enquiry you agree to be contacted about it and acknowledge the Personal Lease Terms & Conditions and Privacy Notice. Approval is subject to application review, vehicle availability and the final signed agreement.</p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-black/10 bg-white p-4 sm:p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
              <h3 className="font-heading font-bold text-lg text-[#121212] mb-1">Get your Personal Lease quote</h3>
              <p className="font-body text-sm text-[#666666] mb-4">Tell us what you need and our team will recommend suitable vehicles and prepare your quotation.</p>
              <LeadConnectForm formId={LEASING_FORM_ID} formName="Leasing Services Form" height={760} testid="personal-lease-form" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
